import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Typography, Upload, Button, message, Divider, Modal, Alert } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { PlusOutlined } from '@ant-design/icons';
import { MAX_GALLERY_IMAGES } from '@/constants/AppConfig';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import GalleryLivePreview from './GalleryLivePreview';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/AppRedux/reducers';
import { useParams } from 'react-router-dom';
import { requestHotelProfileGalleryUpdate, clearHotelProfileProcess, hotelProfileContentRequest, processAction } from "@/AppRedux/actions/admin/hotelProfileConfig";
import { uploadSingleImageAndGetDownloadUrl } from "@/utils/ImagesUploadToS3";
import { resizeImage, resizeImageToFile } from "@/utils/imageUtils";
import { UploadGalleryStyle } from '@/components/ui/GalleryContainerStyle';
const { Title } = Typography;


interface DraggableUploadListItemProps {
  originNode: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  file: UploadFile<any>;
}

const DraggableUploadListItem = ({ originNode, file }: DraggableUploadListItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.uid });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    zIndex: isDragging ? 9999 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {React.cloneElement(originNode, { style: { ...originNode.props.style, opacity: isDragging ? 0.5 : 1 } })}
    </div>
  );
};


const GalleryPage: React.FC = () => {
  /* Get propcode from url */
  const { propcode } = useParams<{ propcode: string }>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const objectUrlsRef = useRef<string[]>([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const dispatch = useDispatch();
  const hotelProfileConfig = useSelector((state: RootState) => state.hotelProfileConfig);

  useEffect(() => {
    if (!hotelProfileConfig.dataSource)
      dispatch(hotelProfileContentRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!hotelProfileConfig.dataSource?.images) {
      setFileList([]);
      return;
    }
    const initialFiles = hotelProfileConfig.dataSource?.images.map((image, index: number) => ({
      status: 'done' as const,
      uid: `image-${index}`,
      name: `Image ${index + 1}`,
      url: image,
    }));
    setFileList(initialFiles);
  }, [hotelProfileConfig]);


  useEffect(() => {
    if (hotelProfileConfig.process.action && processAction.includes(hotelProfileConfig.process.action)) {

      if (hotelProfileConfig.process.action === "HOTEL_PROFILE_GALLERY_UPDATE") {

        if (hotelProfileConfig.process.status)
          message.success(hotelProfileConfig.process.message)
        else
          Modal.error({
            title: 'Error',
            content: hotelProfileConfig.process.message || 'An error occurred while processing.',
          });
      }
      dispatch(clearHotelProfileProcess());

    }
  }, [hotelProfileConfig.process, dispatch]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleCancel = () => setPreviewOpen(false);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await resizeImage(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setFileList((prev) => {
        const activeIndex = prev.findIndex((i) => i.uid === active.id);
        const overIndex = prev.findIndex((i) => i.uid === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
      message.success('Image order updated.');
    }
  };

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    const processedList = newFileList.map((file) => {
      if (file.originFileObj && !file.url) {
        const url = URL.createObjectURL(file.originFileObj);
        objectUrlsRef.current.push(url);
        return { ...file, url, status: 'done' as const };
      }
      return file;
    });
    setFileList(processedList);
  };

  const beforeUpload = (file: RcFile, batch: RcFile[]) => {
    if (fileList.length + batch.length > MAX_GALLERY_IMAGES) {
      if (file === batch[0]) {
        message.error(
          `Cannot upload more than ${MAX_GALLERY_IMAGES} images. Please remove some images first.`
        );
      }
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const formattedImages = await Promise.all(fileList.map(async (file) => {
        if (file.originFileObj) {
          const resizedFile = await resizeImageToFile(file.originFileObj as RcFile, 1920, 0.85);
          return await uploadSingleImageAndGetDownloadUrl({
            propCode: propcode === "newsoft" ? "sample" : propcode || "",
            file: resizedFile,
          });
        }
        return (file.url || '').toString();
      }));

      const jsonData = { images: formattedImages.filter(Boolean) };
      dispatch(requestHotelProfileGalleryUpdate(jsonData));
    } catch (error) {
      console.error("Save failed:", error);
      Modal.error({
        title: 'Upload Failed',
        content: 'Image upload failed. Please try again or contact support if the problem persists.',
      });
      return; // Stop the submission
    } finally {
      setLoading(false);
    }
  };

  const previewImages = useMemo(() => {
    return fileList.map(file => file.url || '').filter(url => url);
  }, [fileList]);

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Card>
        <Title level={2}>Gallery Setup for Hero Carousel</Title>
        <p>Drag and drop to reorder images. Upload, preview, and delete images for the main carousel.</p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={fileList.map((f) => f.uid)} strategy={rectSortingStrategy}>
            <UploadGalleryStyle>
              <Upload
                accept="image/*"
                listType="picture-card"
                fileList={fileList}
                multiple
                onPreview={handlePreview}
                onChange={handleChange}
                beforeUpload={beforeUpload}
                itemRender={(originNode, file) => (
                  <DraggableUploadListItem originNode={originNode} file={file} />
                )}
              >
                {fileList.length >= MAX_GALLERY_IMAGES ? null : uploadButton}
              </Upload>
            </UploadGalleryStyle>
          </SortableContext>
        </DndContext>

        {fileList.length >= MAX_GALLERY_IMAGES ? (
          <Alert
            type="warning"
            showIcon
            title={`Maximum ${MAX_GALLERY_IMAGES} images reached`}
            description="Delete an image to upload a new one."
            style={{ marginTop: 12 }}
          />
        ) : fileList.length > 0 && (
          <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
            {fileList.length} image{fileList.length !== 1 ? 's' : ''} uploaded
          </div>
        )}

        <Button
          type="primary"
          style={{ marginTop: 16 }}
          onClick={handleSave}
          loading={loading}
        >
          Save Changes
        </Button>

        <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
          <img alt="example" style={{ width: '100%' }} src={previewImage} />
        </Modal>

        <Divider />

        <Title level={3}>Live Preview</Title>
        <GalleryLivePreview images={previewImages} />
      </Card>
    </div>
  );
};

export default GalleryPage;
